<template>
  <v-menu v-model="showDateFilter" offset-y :close-on-content-click="false" transition="scale-transition">
    <template #activator="{ on, attrs }">
      <v-btn v-bind="attrs" v-on="on" class="mr-2 date-filter__button" outlined>
        <v-icon class="mr-md-1 date-filter__icon">mdi-calendar</v-icon>
        <span class="d-none d-md-inline">{{ dateFilterLabel }}</span>
      </v-btn>
    </template>
    <div class="date-filter font">
      <div class="date-filter__option" @click="setYesterday">За вчера</div>
      <div class="date-filter__option" @click="setToday">За сегодня</div>
      <div class="date-filter__option" @click="setThisWeek">За неделю</div>
      <div class="date-filter__option" @click="setThisMonth">За месяц</div>
      <div class="date-filter__option" @click="setAllTime">За всё время</div>
      <div class="date-filter__filter d-flex">
        <v-menu v-model="startDateMenu" offset-y transition="scale-transition">
          <template #activator="{ on, attrs }">
            <v-text-field
              class="date-filter__date-field mr-2"
              dense
              outlined
              v-model="startDateFormatted"
              hide-details
              prepend-inner-icon="mdi-calendar"
              v-bind="attrs"
              readonly
              v-on="on"
              @click:prepend-inner="startDateMenu = !startDateMenu"
            ></v-text-field>
          </template>
          <v-date-picker
            class="date-filter__picker"
            :allowed-dates="isBefore"
            :first-day-of-week="1"
            locale="ru-RU"
            v-model="start"
            no-title
            @input="startDateMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-menu v-model="endDateMenu" offset-y transition="scale-transition">
          <template #activator="{ on, attrs }">
            <v-text-field
              class="date-filter__date-field mr-2"
              dense
              outlined
              v-model="endDateFormatted"
              hide-details
              prepend-inner-icon="mdi-calendar"
              v-bind="attrs"
              readonly
              v-on="on"
              @click:prepend-inner="endDateMenu = !endDateMenu"
            ></v-text-field>
          </template>
          <v-date-picker
            class="date-filter__picker"
            :allowed-dates="isAfter"
            :first-day-of-week="1"
            locale="ru-RU"
            v-model="end"
            no-title
            @input="endDateMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-btn color="primary" elevation="0" @click="setArbitraryPeriod">ok</v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script>
import {
  isSameDay,
  isMonday,
  isSunday,
  isThisWeek,
  isToday,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isThisMonth,
  isAfter,
  isBefore,
  isEqual,
  formatISO,
  endOfWeek,
  startOfWeek,
  endOfMonth,
  startOfMonth,
  isSameMonth,
  startOfYesterday,
  isYesterday,
} from "date-fns";

export default {
  name: "DateFilter",
  props: {
    value: Object | null,
  },
  data() {
    return {
      startDateMenu: false,
      endDateMenu: false,
      showDateFilter: false,
    };
  },
  computed: {
    start: {
      get() {
        return (this.value && this.value.start) || "";
      },
      set(value) {
        this.$emit("change-date", { start: value, end: this.end });
      },
    },

    end: {
      get() {
        return (this.value && this.value.end) || "";
      },
      set(value) {
        this.$emit("change-date", { start: this.start, end: value });
      },
    },

    startDateFormatted() {
      return this.formatDate(this.start);
    },

    endDateFormatted() {
      return this.formatDate(this.end);
    },

    dateFilterLabel() {
      // Фильтр по дате не назначен
      if (!this.start && !this.end) {
        return "за все время";
      }

      // Фильтр установлен на период
      if (this.start && this.end) {
        const startDate = new Date(this.start);
        const endDate = new Date(this.end);

        // Сегодня
        if (isToday(startDate) && isToday(endDate)) {
          return "За сегодня";
        }
        if (isYesterday(startDate) && isYesterday(endDate)) {
          return "За вчера";
        }

        // Эта неделя
        if (isMonday(startDate) && isSunday(endDate) && isThisWeek(startDate)) {
          return "За неделю";
        }

        // Этот месяц
        if (isFirstDayOfMonth(startDate) && isLastDayOfMonth(endDate) && isThisMonth(startDate)) {
          return "За месяц";
        }

        // Один день
        if (isSameDay(startDate, endDate)) {
          return this.formatDate(this.start);
        }

        // Произвольная дата
        return `c ${this.formatDate(this.start)} по ${this.formatDate(this.end)}`;
      }

      // Фильтр по дате установлен только на один день "с" или "до"
      if (this.start) {
        return `c ${this.formatDate(this.start)}`;
      }

      if (this.end) {
        return `по ${this.formatDate(this.end)}`;
      }
    },
  },

  methods: {
    applyDateFilter({ start, end }) {
      this.$emit("change", { start, end });
      this.showDateFilter = false;
    },
    isAfter(val) {
      if (!this.start) return true;

      const date = new Date(val);
      const start = new Date(this.start);
      return isAfter(date, start) || isEqual(date, start);
    },
    isBefore(val) {
      if (!this.end) return true;

      const date = new Date(val);
      const end = new Date(this.end);
      return isBefore(date, end) || isEqual(date, end);
    },
    setYesterday() {
      const yesterday = this.formatDateISO(startOfYesterday());
      this.applyDateFilter({ start: yesterday, end: yesterday });
    },
    setToday() {
      const today = this.formatDateISO(new Date());
      this.applyDateFilter({ start: today, end: today });
    },
    setThisWeek() {
      const today = new Date();
      const start = this.formatDateISO(startOfWeek(today, { weekStartsOn: 1 }));
      const end = this.formatDateISO(endOfWeek(today, { weekStartsOn: 1 }));
      this.applyDateFilter({ start, end });
    },
    setThisMonth() {
      const today = new Date();
      const start = this.formatDateISO(startOfMonth(today));
      const end = this.formatDateISO(endOfMonth(today));
      this.applyDateFilter({ start, end });
    },
    setAllTime() {
      this.applyDateFilter({ start: "", end: "" });
    },
    setArbitraryPeriod() {
      // выбранные даты уже находятся в стейте (и здесь через геттер)
      this.applyDateFilter({ start: this.start, end: this.end });
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}.${month}.${year}`;
    },
    formatDateISO(date) {
      return formatISO(date, { representation: "date" });
    },
  },
};
</script>

<style lang="scss">
.date-filter {
  background-color: #ffffff;

  &__button.v-btn:not(.v-btn--round).v-size--default {
    @media (max-width: 960px) {
      min-width: 0;
      padding: 0 8px;
    }
  }

  &__button .date-filter__icon {
    @media (max-width: 600px) {
      font-size: 18px;
    }
  }

  &__picker {
    & .theme--light.v-btn.v-btn--disabled {
      color: #dadada !important;
    }
  }

  &__date {
    padding: 10px;
    border: 1px solid #cbcdd1;
    border-radius: 3px;
  }

  &__filter {
    padding: 16px;
  }

  &__date-field {
    font-size: 14px;
    width: 125px;

    &.v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 36px;
    }

    &.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__prepend-inner {
      margin-top: 6px;
    }

    & i.v-icon {
      font-size: 20px;
    }
  }

  &__option {
    cursor: pointer;
    padding: 12px 16px;
    border-bottom: 1px solid #e6e7e9;

    &:hover {
      background-color: #eeeeee;
    }
  }
}
</style>
